import { useEffect, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { useModeStore } from '@stores/modeStore.js'
import { useThemeStore } from '@stores/themeStore.js'
import { ALL_NAV_SECTIONS } from './navConfig.js'
import { ROUTES } from '@utils/constants.js'
import { markModeExplicit } from '@utils/modePreference.js'
import { cfIconSvgProps } from '@utils/iconSvg.js'
import { NavIcon } from './NavIcons.js'
import { countAlertasAtivos } from '@services/crud.js'

interface Props {
  open: boolean
  onClose: () => void
}

function IconHome({ size = 20 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)}>
      <path d='M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z' />
    </svg>
  )
}

function IconSun({ size = 20 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)}>
      <circle cx='12' cy='12' r='4' />
      <path d='M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42' />
    </svg>
  )
}

function IconMoon({ size = 20 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)}>
      <path d='M21 14.5A8.5 8.5 0 1 1 9.5 3 6.5 6.5 0 0 0 21 14.5z' />
    </svg>
  )
}

function IconSystem({ size = 20 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)}>
      <rect x='2' y='4' width='20' height='14' rx='2' />
      <path d='M8 20h8M12 18v2' />
    </svg>
  )
}

const THEME_TOOLS = [
  { id: 'light' as const, label: 'Claro', Icon: IconSun },
  { id: 'dark' as const, label: 'Escuro', Icon: IconMoon },
  { id: 'system' as const, label: 'Sistema', Icon: IconSystem },
]

export function NavDrawer({ open, onClose }: Props) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const setMode = useModeStore((s) => s.setMode)
  const themeMode = useThemeStore((s) => s.mode)
  const setThemeMode = useThemeStore((s) => s.setMode)
  const [alertas, setAlertas] = useState(0)

  useEffect(() => {
    if (!open) return
    countAlertasAtivos().then(setAlertas)
  }, [open])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const go = useCallback(
    (to: string, navMode?: 'guia' | 'obra') => {
      if (navMode) {
        markModeExplicit()
        setMode(navMode)
      }
      navigate(to)
      onClose()
    },
    [navigate, onClose, setMode],
  )

  const isItemActive = useCallback(
    (to: string, end?: boolean) => (end ? pathname === to : pathname.startsWith(to)),
    [pathname],
  )

  if (!open) return null

  const drawer = (
    <div className='cf3-nav-drawer' role='dialog' aria-modal aria-label='Menu de navegação'>
      <button
        type='button'
        className='cf3-nav-drawer__backdrop'
        onClick={onClose}
        aria-label='Fechar painel'
      />
      <div className='cf3-nav-drawer__sheet'>
        <div className='cf3-nav-drawer__head'>
          <p className='cf3-nav-drawer__title'>Menu</p>
          <div className='cf3-nav-drawer__toolbar'>
            <button
              type='button'
              className={[
                'cf3-nav-drawer__tool',
                pathname === ROUTES.HOME ? 'cf3-nav-drawer__tool--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => go(ROUTES.HOME)}
              aria-label='Início'
            >
              <IconHome />
            </button>
            {THEME_TOOLS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type='button'
                className={[
                  'cf3-nav-drawer__tool',
                  themeMode === id ? 'cf3-nav-drawer__tool--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setThemeMode(id)}
                aria-label={label}
                aria-pressed={themeMode === id}
              >
                <Icon />
              </button>
            ))}
          </div>
        </div>

        {ALL_NAV_SECTIONS.map((section) => (
          <div
            key={section.id}
            className='cf3-nav-drawer__section'
            data-mode={section.mode ?? 'app'}
          >
            <div className='cf3-nav-drawer__section-head'>
              <p className='cf3-nav-drawer__section-label'>{section.label}</p>
              <p className='cf3-nav-drawer__section-desc'>{section.desc}</p>
            </div>
            <ul className='cf3-nav-drawer__list'>
              {section.items.map((item) => {
                const active = isItemActive(item.to, item.end)
                const badge = item.showAlertBadge && alertas > 0 ? alertas : 0
                return (
                  <li key={`${section.id}-${item.to}`}>
                    <button
                      type='button'
                      className={[
                        'cf3-nav-drawer__item',
                        active ? 'cf3-nav-drawer__item--active' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => go(item.to, section.mode)}
                    >
                      <span className='cf3-nav-drawer__item-icon' aria-hidden>
                        <NavIcon icon={item.icon} size={20} />
                      </span>
                      <span className='cf3-nav-drawer__item-label'>{item.label}</span>
                      {badge > 0 && (
                        <span className='cf3-nav-drawer__item-badge'>
                          {badge > 9 ? '9+' : badge}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )

  const shell = typeof document !== 'undefined' ? document.getElementById('app') : null
  return shell ? createPortal(drawer, shell) : drawer
}
